import React from 'react';
import clsx from 'clsx';
import Tooltip from '@srm/ui/tooltip';

interface IProps {
    /** Предложенное участником количество. */
    quantity: number | null;
    /** Требуемое количество по позиции. */
    positionQuantity: number | null;
    /** Единица измерения (ОКЕИ). */
    unit: string | null;
}

const fmt = (n: number): string => n.toLocaleString('ru-RU', { maximumFractionDigits: 4 });

/* SVG-геометрия пирога: viewBox 0..32 (2× для чёткости), центр 16, радиус 14.5. */
const C = 16;
const R = 14.5;

/** Точка на окружности: frac 0..1, 0 = верх (12 ч), по часовой стрелке. */
const polar = (frac: number): [number, number] => {
    const a = (frac * 360 - 90) * (Math.PI / 180);
    return [C + R * Math.cos(a), C + R * Math.sin(a)];
};

/** SVG-путь сектора пирога [f0..f1] (доли 0..1) от центра. f1−f0 строго < 1. */
const sector = (f0: number, f1: number): string => {
    const [x0, y0] = polar(f0);
    const [x1, y1] = polar(f1);
    const large = f1 - f0 > 0.5 ? 1 : 0;
    return `M${C},${C} L${x0.toFixed(3)},${y0.toFixed(3)} A${R},${R} 0 ${large} 1 ${x1.toFixed(3)},${y1.toFixed(3)} Z`;
};

/* Доля отклонения на значке фиксирована: на 16 px пропорция всё равно не
   считывается (1% и 8% — одна и та же щель), а постоянный размер делает значок
   узнаваемой пиктограммой. Величину отклонения даёт тултип. */
const DEVIATION = 0.2;

/**
 * Индикатор недо-/перепоставки в заголовке предложения — мини-пайчарт (SVG),
 * сравнивающий предложенное количество с требуемым по позиции.
 *
 * - предложено меньше требуемого → недопоставка: оранжевый круг с пустой
 *   выемкой (недостача);
 * - предложено больше → перепоставка: слабо-оранжевый круг (требуемое) с
 *   оранжевой долькой (излишек);
 * - размер выемки/дольки постоянный (см. DEVIATION) — значок сообщает вид
 *   отклонения, величину даёт тултип;
 * - ровно столько → значок не рисуется: соответствие количества — норма,
 *   а не новость, значок остаётся признаком отклонения.
 *
 * Рендер: базовый круг + (опц.) один сектор-оверлей + обводка. Цвета заданы
 * inline-style'ом (CSS-переменные резолвятся, в отличие от fill-атрибута).
 *
 * Тултип содержит количества (предложено из требуемого) и отклонение.
 * Если данных нет / требуемое ≤ 0 — ничего не рендерит.
 */
export const DeliveryPie: React.FC<IProps> = ({ quantity, positionQuantity, unit }) => {
    if (quantity == null || positionQuantity == null || positionQuantity <= 0 || quantity < 0) {
        return null;
    }

    const u = unit ? ` ${unit}` : '';
    const qtyLine = `Предложено: ${fmt(quantity)}${u} из ${fmt(positionQuantity)}${u}`;
    const EPS = 1e-9;

    let status: 'under' | 'over';
    let baseColor: string;
    let overlay: { path: string; color: string };
    let title: string;
    let detail: string;

    if (quantity < positionQuantity - EPS) {
        status = 'under';
        /* Пустой трек = выемка недостачи (слева сверху), оранжевый оверлей —
           остальной круг. Долька излишка при перепоставке стоит справа сверху:
           нехватка «слева», избыток «справа» — стороны разводят два случая. */
        baseColor = 'var(--ws-delivery-track)';
        overlay = { path: sector(0, 1 - DEVIATION), color: 'var(--ws-delivery-under)' };
        const pct = Math.round(((positionQuantity - quantity) / positionQuantity) * 100);
        title = 'Недопоставка';
        detail = `Недостаёт: ${fmt(positionQuantity - quantity)}${u} (−${pct}%)`;
    } else if (quantity > positionQuantity + EPS) {
        status = 'over';
        /* Слабо-оранжевый круг = требуемое, оранжевая долька = излишек. */
        baseColor = 'var(--ws-delivery-over-base)';
        overlay = { path: sector(0, DEVIATION), color: 'var(--ws-delivery-over)' };
        const pct = Math.round(((quantity - positionQuantity) / positionQuantity) * 100);
        title = 'Перепоставка';
        detail = `Излишек: ${fmt(quantity - positionQuantity)}${u} (+${pct}%)`;
    } else {
        return null;
    }

    return (
        <Tooltip
            openDelay={100}
            background="black"
            position="bottom"
            label={`${title}\n${qtyLine}${detail ? `\n${detail}` : ''}`}
        >
            <svg
                className={clsx('ws-delivery', `ws-delivery--${status}`)}
                viewBox="0 0 32 32"
                role="img"
                aria-label={`${title}. ${qtyLine}`}
            >
                <circle cx={C} cy={C} r={R} style={{ fill: baseColor }} />
                <path d={overlay.path} style={{ fill: overlay.color }} />
                <circle
                    cx={C}
                    cy={C}
                    r={R}
                    fill="none"
                    strokeWidth={1.25}
                    style={{ stroke: 'var(--ws-delivery-stroke)' }}
                />
            </svg>
        </Tooltip>
    );
};
